import React, { FC, useCallback, useState } from 'react';
import { Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { BillingDetails } from '../../shared/orders/payment';
import { useTranslation } from '../translations';
import { useUser, useUserSafe } from '../utils';
import { CountrySelector } from './CountrySelector';
import { MaterialIconText } from './MaterialIconText';
import { RegionSelector } from './RegionSelector';

import './BillingAddress.scss';

interface Props {
  readonly billingDetails: BillingDetails;
  readonly updateBillingDetails: (field: string, value: string | null) => void;
}

export const BillingAddress: FC<Props> = ({ billingDetails, updateBillingDetails }) => {
  const [isEditing, setIsEditing] = useState(false);
  const user = useUser();

  const resetBillingDetails = useCallback(() => {
    updateBillingDetails('reset', null);
    setIsEditing(false);
  }, [updateBillingDetails]);

  if (!user) {
    return null;
  }

  return (
    <Card id="transactionBilling">
      <CardBody>
        {isEditing ? (
          <EditBillingAddress
            billingDetails={billingDetails}
            resetBillingDetails={resetBillingDetails}
            updateBillingDetails={updateBillingDetails}
          />
        ) : (
          <BillingAddressSummary
            editBillingAddress={() => {
              setIsEditing(true);
            }}
          />
        )}
      </CardBody>
    </Card>
  );
};

interface SummaryProps {
  readonly editBillingAddress: () => void;
}

const BillingAddressSummary: FC<SummaryProps> = ({ editBillingAddress }) => {
  const user = useUserSafe();
  const { ts } = useTranslation();

  return (
    <>
      <div className="billing-address-header">
        <h5>{ts('common.billing_address')}</h5>
        <div className="billing-address-usealt" onClick={editBillingAddress}>
          <MaterialIconText name="edit" small>
            {ts('use_different_billing_address')}
          </MaterialIconText>
        </div>
      </div>
      <div className="billing-address-summary">
        {user.firstName} {user.lastName}, {user.addressLine1},{' '}
        {user.addressLine2 && `${user.addressLine2}, `}
        {user.addressCity}, {user.addressState}, {user.addressCountry}, {user.addressZipcode}
      </div>
    </>
  );
};

interface EditProps extends Props {
  readonly resetBillingDetails: () => void;
}

const EditBillingAddress: FC<EditProps> = ({
  billingDetails,
  resetBillingDetails,
  updateBillingDetails,
}) => {
  const { ts } = useTranslation();

  return (
    <>
      <div className="billing-address-header">
        <h5>{ts('common.billing_address')}</h5>
        <div className="billing-address-usealt" onClick={resetBillingDetails}>
          <MaterialIconText name="history" small>
            {ts('reset_to_account_details')}
          </MaterialIconText>
        </div>
      </div>
      <Row>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingFirstName">{ts('common.first_name')}</Label>
            <Input
              defaultValue={billingDetails.firstName}
              onChange={(e) => {
                updateBillingDetails('firstName', e.target.value);
              }}
              required
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingLastName">{ts('common.last_name')}</Label>
            <Input
              defaultValue={billingDetails.lastName}
              onChange={(e) => {
                updateBillingDetails('lastName', e.target.value);
              }}
              required
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingAddressLine1">{ts('common.address_line_1')}</Label>
            <Input
              defaultValue={billingDetails.addressLine1}
              onChange={(e) => {
                updateBillingDetails('addressLine1', e.target.value);
              }}
              required
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingAddressLine2">{ts('common.address_line_2_optional')}</Label>
            <Input
              defaultValue={billingDetails.addressLine2 ?? ''}
              onChange={(e) => {
                updateBillingDetails('addressLine2', e.target.value === '' ? null : e.target.value);
              }}
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingAddressCity">{ts('common.city')}</Label>
            <Input
              defaultValue={billingDetails.city}
              onChange={(e) => {
                updateBillingDetails('city', e.target.value);
              }}
              required
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingAddressZipcode">{ts('common.zip_postal_code')}</Label>
            <Input
              defaultValue={billingDetails.zipcode}
              id="billingAddressZipcode"
              onChange={(e) => {
                updateBillingDetails('zipcode', e.target.value);
              }}
              required
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingAddressCountry">{ts('common.country')}</Label>
            <CountrySelector
              autoComplete="country-name"
              className="form-control"
              id="billingAddressCountry"
              name="billingAddressCountry"
              onChange={(val) => {
                updateBillingDetails('country', val);
              }}
              value={billingDetails.country}
              valueType="short"
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label for="billingAddressState">{ts('common.state_province')}</Label>
            <RegionSelector
              className="form-control"
              country={billingDetails.country}
              countryValueType="short"
              data-coerce={false}
              disableWhenEmpty
              id="billingAddressState"
              name="billingAddressState"
              onChange={(val) => {
                updateBillingDetails('state', val);
              }}
              value={billingDetails.state}
              valueType="short"
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};
